import { useState } from "react";
import { api } from "@/lib/api";
import {
  Radar,
  Phone,
  MapPin,
  Loader2,
  ShieldCheck,
  AlertTriangle,
  Zap,
  Sparkles,
} from "lucide-react";
import { toast } from "sonner";

// ---------------------------------------------------------------
// Amanda · Local Scan Intake
// Fires the 60-second Lead Leak scan from a phone + address.
// Backend auto-detects industry and returns 3 leaks + next step.
// Stored in lead_hunts with practice=True on the server.
// ---------------------------------------------------------------

export default function AmandaLocalScan() {
  const [phone, setPhone] = useState("");
  const [address, setAddress] = useState("");
  const [hint, setHint] = useState("");
  const [scanning, setScanning] = useState(false);
  const [result, setResult] = useState(null);

  const canScan = address.trim().length >= 4 && !scanning;

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!canScan) return;
    setScanning(true);
    setResult(null);
    try {
      const { data } = await api.post("/amanda/local-scan", {
        phone: phone.trim(),
        address: address.trim(),
        business_hint: hint.trim(),
      });
      setResult(data);
      // Persist the industry tag so the Grid Concierge can personalise Amanda.
      if (data?.industry) {
        try {
          sessionStorage.setItem("grid_industry_tag", data.industry);
        } catch (_) { /* ignore */ }
      }
      toast.success("Amanda finished the scan — briefing below.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : "Scan failed — try again.";
      toast.error(msg);
    } finally {
      setScanning(false);
    }
  };

  const reset = () => {
    setResult(null);
    setPhone("");
    setAddress("");
    setHint("");
  };

  return (
    <section
      className="rounded-sm border relative overflow-hidden mb-6"
      style={{
        borderColor: "rgba(167,139,250,0.4)",
        background:
          "linear-gradient(180deg, rgba(167,139,250,0.10) 0%, rgba(6,11,20,0.85) 100%)",
        boxShadow: "0 24px 60px -24px rgba(167,139,250,0.35)",
      }}
      data-testid="amanda-local-scan"
    >
      <div
        className="absolute inset-x-0 top-0 h-px"
        style={{
          background:
            "linear-gradient(90deg, transparent, rgba(167,139,250,0.85), transparent)",
        }}
      />

      <div className="p-5 sm:p-6">
        <div className="flex items-center gap-3 mb-2">
          <div
            className="w-10 h-10 rounded-sm border flex items-center justify-center shrink-0"
            style={{
              borderColor: "rgba(167,139,250,0.55)",
              background: "rgba(167,139,250,0.18)",
              color: "#A78BFA",
            }}
          >
            <Radar size={18} />
          </div>
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.32em] text-[#A78BFA] mb-0.5 flex items-center gap-2">
              <Sparkles size={10} /> Amanda · Local Scan
            </div>
            <div className="font-display text-white text-lg sm:text-xl leading-tight">
              Drop a phone and address —{" "}
              <span className="text-[#A78BFA] italic">I&apos;ll find the bleed in 60 seconds.</span>
            </div>
          </div>
        </div>

        {!result ? (
          <form onSubmit={submit} className="mt-4 space-y-3" data-testid="amanda-local-scan-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="hud-label text-[10px] mb-1.5 flex items-center gap-1.5">
                  <Phone size={11} /> Phone (optional)
                </label>
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="(614) 555-0142"
                  data-testid="amanda-local-scan-phone"
                  className="w-full bg-[#060B14] border border-white/10 rounded-sm px-4 py-3 text-white text-sm focus:outline-none focus:border-[#A78BFA] min-h-[44px]"
                  maxLength={40}
                  autoComplete="tel"
                />
              </div>
              <div>
                <label className="hud-label text-[10px] mb-1.5 flex items-center gap-1.5">
                  <MapPin size={11} /> Address <span className="text-[#A78BFA]">*</span>
                </label>
                <input
                  type="text"
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  placeholder="112 W Center St, Marion OH"
                  data-testid="amanda-local-scan-address"
                  className="w-full bg-[#060B14] border border-white/10 rounded-sm px-4 py-3 text-white text-sm focus:outline-none focus:border-[#A78BFA] min-h-[44px]"
                  maxLength={200}
                  autoComplete="street-address"
                  required
                />
              </div>
            </div>
            <div>
              <label className="hud-label text-[10px] mb-1.5 flex items-center gap-1.5">
                Business hint <span className="text-slate-500 normal-case tracking-normal">(optional — helps Amanda tag faster)</span>
              </label>
              <input
                type="text"
                value={hint}
                onChange={(e) => setHint(e.target.value)}
                placeholder="e.g. Motorcycle shop, Roofer, Salon…"
                data-testid="amanda-local-scan-hint"
                className="w-full bg-[#060B14] border border-white/10 rounded-sm px-4 py-3 text-white text-sm focus:outline-none focus:border-[#A78BFA] min-h-[44px]"
                maxLength={120}
              />
            </div>
            <button
              type="submit"
              disabled={!canScan}
              data-testid="amanda-local-scan-submit"
              className="w-full py-4 rounded-sm text-sm uppercase tracking-[0.24em] font-display flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
              style={{
                background: canScan
                  ? "linear-gradient(90deg, #A78BFA 0%, #7C3AED 100%)"
                  : "rgba(167,139,250,0.2)",
                color: "#F8FAFC",
                border: "1px solid rgba(167,139,250,0.7)",
                boxShadow: canScan ? "0 0 22px -6px rgba(167,139,250,0.6)" : "none",
              }}
            >
              {scanning ? (
                <>
                  <Loader2 size={14} className="animate-spin" /> Scanning the block…
                </>
              ) : (
                <>
                  <Zap size={14} /> Fire the 60-Second Scan
                </>
              )}
            </button>
            <p className="text-[10px] uppercase tracking-[0.24em] font-mono-aviation text-slate-500">
              · Practice-mode scan · No live pitch fires · Human follow-up within 24h
            </p>
          </form>
        ) : (
          <ScanResult result={result} onReset={reset} />
        )}
      </div>
    </section>
  );
}

function ScanResult({ result, onReset }) {
  const briefing = result?.briefing || {};
  const leaks = Array.isArray(briefing.leaks) ? briefing.leaks : [];
  const headline = briefing.headline || "Amanda has your file.";
  const nextStep = briefing.next_step || "";
  const industry = result?.industry || "Local Shop";

  return (
    <div className="mt-4 space-y-4" data-testid="amanda-local-scan-result">
      {/* Industry tag */}
      <div
        className="rounded-sm border px-4 py-3 flex items-center gap-3"
        style={{
          borderColor: "rgba(52,211,153,0.45)",
          background: "rgba(52,211,153,0.08)",
        }}
      >
        <ShieldCheck size={16} className="text-emerald-300 shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-[0.28em] text-emerald-300 font-mono-aviation">
            Industry Tag Locked
          </div>
          <div className="font-display text-white text-base" data-testid="amanda-scan-industry">
            {industry}
          </div>
        </div>
      </div>

      {/* Headline */}
      {headline && (
        <div
          className="rounded-sm border border-white/10 p-4 bg-black/40"
          data-testid="amanda-scan-headline"
        >
          <div className="hud-label text-[10px] mb-2">Amanda&apos;s Briefing</div>
          <p className="text-white text-sm leading-relaxed font-display">{headline}</p>
        </div>
      )}

      {/* Leaks */}
      {leaks.length > 0 && (
        <div className="space-y-2" data-testid="amanda-scan-leaks">
          <div className="hud-label text-[10px] flex items-center gap-2 text-[#F5C842]">
            <AlertTriangle size={11} /> Three Leaks Detected
          </div>
          {leaks.map((leak, i) => (
            <div
              key={`leak-${i}`}
              className="flex items-start gap-3 rounded-sm border p-3"
              style={{
                borderColor: "rgba(244,63,94,0.4)",
                background: "rgba(244,63,94,0.06)",
              }}
              data-testid={`amanda-scan-leak-${i}`}
            >
              <span
                className="w-6 h-6 rounded-sm border shrink-0 flex items-center justify-center text-[10px] font-mono-aviation"
                style={{
                  borderColor: "rgba(244,63,94,0.6)",
                  color: "#F87171",
                  background: "rgba(244,63,94,0.12)",
                }}
              >
                {String(i + 1).padStart(2, "0")}
              </span>
              <span className="text-sm text-slate-200 leading-relaxed">{leak}</span>
            </div>
          ))}
        </div>
      )}

      {/* Next step */}
      {nextStep && (
        <div
          className="rounded-sm border p-4"
          style={{
            borderColor: "rgba(212,175,55,0.55)",
            background: "linear-gradient(180deg, rgba(212,175,55,0.10) 0%, rgba(6,11,20,0.6) 100%)",
          }}
          data-testid="amanda-scan-next-step"
        >
          <div className="hud-label text-[10px] text-gold mb-2">Amanda&apos;s Next Move</div>
          <p className="text-white text-sm leading-relaxed">{nextStep}</p>
        </div>
      )}

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="text-[10px] uppercase tracking-[0.24em] font-mono-aviation text-slate-500 break-all">
          Ref · {result?.hunt_id || "—"}
        </div>
        <button
          type="button"
          onClick={onReset}
          data-testid="amanda-local-scan-reset"
          className="btn-ghost-gold px-4 py-2 rounded-sm text-xs uppercase tracking-[0.22em]"
        >
          Run Another Scan
        </button>
      </div>
    </div>
  );
}
